'use client';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import cn from 'classnames';
import { GoogleAnalytics } from 'nextjs-google-analytics';
import { differenceInDays } from 'date-fns';

import './AppShellLayer.style.scss';

import Header, { HeaderCMSContent } from '@/components/common/Header/Header';
import Footer from '@/components/common/Footer/Footer';
import Toaster from '@/components/common/Toaster/Toaster';
import PastDueBanner from '@/components/common/PastDueBanner/PastDueBanner';
import BuilderSiteBanner, {
  BuilderSiteBannerCMSContent
} from '@/components/builder/BuilderSiteBanner/BuilderSiteBanner';
import BuilderSiteModal, {
  BuilderSiteModalCMSContent
} from '@/components/builder/BuilderSiteModal/BuilderSiteModal';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

import { USER_STATUS } from '@/types/User';
import { useOnMount } from '@/components/hook/onMount';
import { useModal } from '@/components/hook/modal';
import analytics from '@/util/analytics';
import BuilderCaptureModal, {
  BuilderCaptureModalCMSContent
} from '@/components/builder/BuilderCaptureModal/BuilderCaptureModal';
import BannerOrchestrator from '@/components/orchestrator/BannerOrchestrator/BannerOrchestrator';

export type AppShellLayerProps = {
  className?: string;
  children: ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  headerCMSContent?: HeaderCMSContent;
  builderAppBannerData?: BuilderSiteBannerCMSContent;
  builderAppModalData?: BuilderSiteModalCMSContent;
  builderAppCaptureModalData?: BuilderCaptureModalCMSContent;
  hiddenAppBanner?: boolean;
};

function AppShellLayer(props: AppShellLayerProps) {
  const {
    className = '',
    children,
    showHeader = true,
    showFooter = true,
    headerCMSContent = { contentKey: 'undefined', headerCards: [] },
    builderAppBannerData,
    builderAppModalData,
    builderAppCaptureModalData,
    hiddenAppBanner
  } = props;
  const { closet, isIOSWebView, user } = useContextBroker();
  const activeBannerConfig = user
    ? builderAppBannerData?.loggedIn
    : builderAppBannerData?.loggedOut;
  const modalTools = useModal();
  const isFirstBuilderModalRender = useRef(true);
  const isFirstBuilderCaptureModalRender = useRef(true);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previousPathname = useRef('');
  const previousSearchParams = useRef('');

  // This handles the builder component banner
  const [displaySiteBanner, setDisplaySiteBanner] = useState<boolean>(false);

  // Tracked here so we can hide BuilderSiteBanner when the Réservé invited
  // banner is showing (Réservé wins on relevance).
  const [isPriveeBannerShowing, setIsPriveeBannerShowing] =
    useState<boolean>(false);

  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] =
    useState<boolean>(false);

  const [isMobileScreenSize, setIsMobileScreenSize] = useState<boolean>(
    (typeof window !== 'undefined' && window.innerWidth < 768) || false
  );

  const handleCloseSiteBanner = () => {
    localStorage.setItem(
      'vivrelleSiteBannerDismissedDate',
      new Date().toDateString()
    );
    setDisplaySiteBanner(false);
  };

  const handleCloseSiteModal = () => {
    localStorage.setItem(
      'vivrelleSiteModalDismissed-' + builderAppModalData?.modalName,
      'true'
    );
  };

  const handleCloseCaptureModal = () => {
    localStorage.setItem(
      'captureModalDismissedDate',
      new Date().toDateString()
    );
  };

  // 402 gets returned from the closet microservice to signify 'Past due`
  const showPastDueBanner = useMemo(() => {
    return (
      user?.status &&
      [USER_STATUS.ACTIVE, USER_STATUS.FROZEN].includes(user.status) &&
      closet.closet?.error?.status === 402
    );
  }, [closet, user?.status]);

  // App Banner logic moved to BannerOrchestrator

  // LOGIC FOR THE BUILDER SITE BANNER
  // Checked on every render, not in the mount-only effect below: client-side
  // navigation (e.g. home → /apply/account) never remounts this layer, so a
  // mount-time check left the banner up on pages that must not show it.
  const siteBannerHiddenOnPath =
    pathname === '/apply' ||
    pathname.startsWith('/apply/') ||
    pathname.startsWith('/offer/') ||
    pathname === '/reserve' ||
    pathname.startsWith('/reserve/') ||
    pathname === '/collection/reserve' ||
    pathname.startsWith('/collection/reserve/') ||
    /black-friday-2025/i.test(pathname) ||
    /black-friday-shannon/.test(pathname);

  useEffect(() => {
    if (!activeBannerConfig?.enabled) {
      setDisplaySiteBanner(false);
      return;
    }

    const siteBannerDismissedDate =
      localStorage.getItem('vivrelleSiteBannerDismissedDate') || '';
    const today = new Date().toDateString();

    if (!activeBannerConfig?.dismissable && !isIOSWebView && !hiddenAppBanner) {
      setDisplaySiteBanner(true);
    } else if (
      !hiddenAppBanner &&
      !isIOSWebView &&
      (!siteBannerDismissedDate ||
        differenceInDays(new Date(siteBannerDismissedDate), new Date(today)) >=
          7)
    ) {
      setDisplaySiteBanner(true);
    }
  }, [activeBannerConfig]);

  // Use effect to see whether window is mobile or desktop style in order to render different components and text
  useEffect(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      // Set initial state
      setIsMobileScreenSize(window.innerWidth < 768);

      // Define the resize handler
      const handleResize = () => {
        setIsMobileScreenSize(window.innerWidth < 768);
      };

      // Add event listener
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const showBuilderModal = useCallback(() => {
    const anchorPoint = isMobileScreenSize ? 'bottom' : 'center';

    modalTools.showModal(
      <BuilderSiteModal
        handleDismiss={() => {
          handleCloseSiteModal();
          modalTools.closeModal();
        }}
        cmsContent={builderAppModalData}
      />,
      {
        topClassName: 'builderModal',
        anchorPoint,
        onClose: handleCloseSiteModal
      }
    );
  }, [modalTools, builderAppModalData, isMobileScreenSize]);

  const showCaptureModal = useCallback(() => {
    const anchorPoint = isMobileScreenSize ? 'bottom' : 'center';

    const onFormSuccess = () => {
      setIsSubmittedSuccessfully(true);
    };

    modalTools.showModal(
      <BuilderCaptureModal
        handleDismiss={() => {
          handleCloseCaptureModal();
          modalTools.closeModal();
        }}
        cmsContent={builderAppCaptureModalData}
        onFormSuccess={onFormSuccess}
      />,
      {
        // After form submission, the modal will have a pink background
        topClassName: isSubmittedSuccessfully
          ? 'builderModal bg-pink'
          : 'builderModal',
        anchorPoint,
        onClose: handleCloseCaptureModal
      }
    );
  }, [
    modalTools,
    builderAppCaptureModalData,
    isMobileScreenSize,
    isSubmittedSuccessfully
  ]);

  // LOGIC FOR THE BUILDER SITE MODAL
  useEffect(() => {
    // Early returns if modal shouldn't show
    if (!builderAppModalData?.enabled) return;
    if (isIOSWebView && !builderAppModalData.showInApp) return;
    if (
      pathname === '/ella' ||
      pathname === '/ella-in-app' ||
      pathname === '/ella-in-store'
    )
      return;
    // Réservé invite landing auto-opens the LoginModal — don't displace it.
    if (pathname === '/reserve' && searchParams.get('invite') === '1') return;

    // Get current modal's dismissed state
    const currentModalKey =
      'vivrelleSiteModalDismissed-' + builderAppModalData.modalName;
    const siteModalDismissed = localStorage.getItem(currentModalKey) || '';
    if (siteModalDismissed) return;

    // Show the modal after specified delay if initial load, otherwise show right away
    const timer = setTimeout(
      () => {
        showBuilderModal();
      },
      isFirstBuilderModalRender.current
        ? (builderAppModalData?.initialDelay ?? 5) * 1000
        : 0
    );

    // Clear other modal dismissed flags
    const prefix = 'vivrelleSiteModalDismissed-';
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Check if key starts with prefix but isn't the current modal
      if (key?.startsWith(prefix) && key !== currentModalKey) {
        localStorage.removeItem(key);
      }
    }

    isFirstBuilderModalRender.current = false;

    // Cleanup timeout on unmount
    return () => clearTimeout(timer);
  }, [
    // Only depend on the specific properties we need
    builderAppModalData?.enabled,
    builderAppModalData?.modalName,
    builderAppModalData?.initialDelay,
    builderAppModalData?.showInApp,
    isIOSWebView,
    showBuilderModal
  ]);

  // LOGIC FOR THE BUILDER CAPTURE MODAL
  useEffect(() => {
    // Early returns if modal shouldn't show
    if (!builderAppCaptureModalData?.enabled) return;
    if (isIOSWebView && !builderAppCaptureModalData.showInApp) return;
    if (pathname === '/privacy-policy' || pathname === '/terms-of-use') return;
    // Réservé invite landing auto-opens the LoginModal — don't displace it.
    if (pathname === '/reserve' && searchParams.get('invite') === '1') return;

    // Check if site modal should be shown - if so, don't show capture modal
    if (builderAppModalData && builderAppModalData?.enabled) {
      const currentModalKey =
        'vivrelleSiteModalDismissed-' + builderAppModalData.modalName;
      const siteModalDismissed = localStorage.getItem(currentModalKey) || '';
      if (!siteModalDismissed) return; // Site modal needs to be shown, so don't show capture modal
    }

    // Is user due to view modal? Either because they've never seen it before,
    // or because it's been at least a month since they've last seen it?
    const today = new Date().toDateString();
    let dueToViewModal = false;
    let captureModalDismissedDate =
      localStorage.getItem('captureModalDismissedDate') || null;

    if (!captureModalDismissedDate) {
      dueToViewModal = true;
    } else {
      dueToViewModal =
        differenceInDays(
          new Date(captureModalDismissedDate),
          new Date(today)
        ) >= (builderAppCaptureModalData.displayFrequency || 30);
    }

    if (dueToViewModal) {
      if (user) {
        // If user is logged in and it's been a month since we saw them last
        // and they aren't opted in either email or sms, show modal
        if (!user.subscriptions.email || !user.subscriptions.sms) {
          dueToViewModal = false;
        }
      }
    }

    if (dueToViewModal) {
      // Show the modal after specified delay if initial load, otherwise show after 5 seconds
      const timer = setTimeout(
        () => {
          showCaptureModal();
        },
        isFirstBuilderCaptureModalRender.current
          ? (builderAppCaptureModalData?.initialDelay ?? 15) * 1000
          : 0
      );

      isFirstBuilderCaptureModalRender.current = false;

      // Cleanup timeout on unmount
      return () => clearTimeout(timer);
    }
  }, [isSubmittedSuccessfully]);

  /* Track page views on client-side navigation */
  useEffect(() => {
    // Get the current search params as a string
    const currentSearchParams = searchParams.toString();

    // If the path or search params have changed, track the page view
    if (
      previousPathname.current !== pathname ||
      previousSearchParams.current !== currentSearchParams
    ) {
      // Track page view on client-side navigation
      analytics.page({
        path: pathname,
        search: currentSearchParams,
        url: pathname + (currentSearchParams ? `?${currentSearchParams}` : '')
      });

      // Update the previous pathname and search params
      previousPathname.current = pathname;
      previousSearchParams.current = currentSearchParams;
    }
  }, [pathname, searchParams]);

  return (
    <div
      id='asl'
      className={cn('AppShellLayer', className)}
    >
      <BannerOrchestrator
        hiddenAppBanner={hiddenAppBanner}
        onPriveeBannerVisibilityChange={setIsPriveeBannerShowing}
      />
      {displaySiteBanner &&
        !siteBannerHiddenOnPath &&
        !isPriveeBannerShowing && (
          <BuilderSiteBanner
            cmsContent={activeBannerConfig}
            handleDismiss={handleCloseSiteBanner}
          />
        )}
      {showHeader && <Header cmsContent={headerCMSContent} />}
      {showPastDueBanner && <PastDueBanner />}

      <main className='AppShellLayer-children'>{children}</main>
      {showFooter && <Footer />}

      <GoogleAnalytics
        trackPageViews
        gaMeasurementId={process.env.NEXT_PUBLIC_GA_ID}
      />
      <Toaster />
    </div>
  );
}

export default AppShellLayer;
